import { useGetLocalizationByCoordinatesLazyQuery, useGetUserCoordinates } from "@stepapp/core";
import type { LocalizationModelItems } from "@stepapp/core/apiTypes";
import {
  Block,
  BottomSheet,
  Button,
  CitySelector,
  CountrySelector,
  Typography,
  spacing,
} from "@stepapp/ui";
import { requireNativeModule } from "expo-modules-core";
import { reloadAsync } from "expo-updates";
import React, { type Dispatch, type SetStateAction, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { useAppLinks } from "../../hooks";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { AppStorage } from "../../services/AppStorage";
import { setAppConfig } from "../../store/appConfig";

type Props = {
  isVisible: boolean;
  setModalVisible: Dispatch<SetStateAction<boolean>>;
};

const CityPickerSheet = ({ isVisible, setModalVisible }: Props) => {
  const { t } = useTranslation();
  const [variant, setVariant] = useState<"country" | "city">("city");
  const visible = useSharedValue(1);
  const countryCode = AppStorage().get("countryCode");
  const appDispatch = useAppDispatch();
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const { ProposeCity } = useAppLinks();

  const handleCityPick = useCallback(
    (localization: LocalizationModelItems) => {
      appDispatch(
        setAppConfig({
          city: localization?.name || "",
          localizationId: localization?.id || "",
          timezone: localization?.timezone || "",
        }),
      );

      setModalVisible(false);
    },
    [appDispatch, setModalVisible],
  );

  const handleCountryPick = useCallback(
    (countryCode: string) => {
      const currentCountryCode = AppStorage().get("countryCode");

      if (!localizationId || currentCountryCode !== countryCode) {
        appDispatch(setAppConfig({ city: "", localizationId: "", timezone: "" }));
        AppStorage().set("countryCode", countryCode);

        setTimeout(() => {
          try {
            requireNativeModule("ExpoUpdates").reload();
          } catch (_) {
            reloadAsync();
          }
        }, 200);
      } else {
        setModalVisible(false);
      }
    },
    [appDispatch, localizationId, setModalVisible],
  );

  const getUserCoordinates = useGetUserCoordinates({ t });

  const [getLocalization] = useGetLocalizationByCoordinatesLazyQuery();

  const fetchLocalization = useCallback(
    async ({
      coordinates,
    }: {
      coordinates?: { latitude: number; longitude: number };
    }) => {
      if (!coordinates) return;

      try {
        const { data: userLocalization } = await getLocalization({
          variables: coordinates,
        });

        if (userLocalization?.getLocalizationByCoordinates?.id) {
          appDispatch(
            setAppConfig({
              city: userLocalization?.getLocalizationByCoordinates?.name || "",
              localizationId: userLocalization?.getLocalizationByCoordinates?.id || "",
              timezone: userLocalization?.getLocalizationByCoordinates?.timezone || "",
            }),
          );
        } else {
          alert(t("alerts.unavailableLocalization"));
        }
      } catch (_error) {}
    },
    [appDispatch, getLocalization, t],
  );

  const toggleBottomSheet = useCallback(async () => {
    setModalVisible(false);

    if (!localizationId) {
      const coordinates = await getUserCoordinates();
      fetchLocalization({ coordinates });
    }
  }, [setModalVisible, localizationId, getUserCoordinates, fetchLocalization]);

  const toggleVariant = useCallback(() => {
    visible.value = withTiming(0, { duration: 300 });

    setTimeout(() => {
      setVariant((prev) => (prev === "country" ? "city" : "country"));
      visible.value = withTiming(1, { duration: 300 });
    }, 300);
  }, [visible]);

  const animatedStyle = useAnimatedStyle(() => ({ opacity: visible.value }));

  return (
    <BottomSheet
      closeOnBackdropPress={!!localizationId}
      contentStyle={styles.container}
      toggle={toggleBottomSheet}
      visible={isVisible}
      id="pickCityModal"
    >
      <Block justifySpace row>
        <Typography variant="newH2">{t("screens.dashboard.chooseCity")}</Typography>

        <Button
          onPress={toggleVariant}
          type="link"
          label={
            variant === "country"
              ? t("screens.pickCity.changeCity")
              : t("screens.pickCity.changeCountry")
          }
        />
      </Block>

      <Typography type="secondary">{t("screens.pickCity.description")}</Typography>

      <Animated.View style={animatedStyle}>
        {variant === "country" ? (
          <CountrySelector countryCode={countryCode} onPress={handleCountryPick} />
        ) : (
          <CitySelector t={t} onPress={handleCityPick} link={ProposeCity} />
        )}
      </Animated.View>
    </BottomSheet>
  );
};

export default React.memo(CityPickerSheet);

const styles = StyleSheet.create({
  container: {
    padding: spacing.unit * 2.5,
  },
});
